import { FC, useEffect, useState } from 'react';
import { IonLabel, IonSegmentButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { useSubscription } from '@meraki-internal/state';
import { createUseStyles } from 'react-jss';
import { ViewNoteMenu } from './ViewNoteMenu';
import { NoteDateTarget } from '../note-list/CareMapNotesListTitle';
import { UserNotes } from './UserNotes';
import { Overview } from './Overview';
import { SmartTasks } from './SmartTasks';
import { Attachments } from './Attachments';
import { DetailedSummary } from './DetailedSummary';
import { Transcription } from './Transcription';
import { Participants } from './Participants';
import { CareMapNoteTracker } from '../model/CareMapNoteTracker';
import { ShareNoteNudge } from './ShareNoteNudge';
import { BeaCommentBubble } from './BeaCommentBubble';
import { AppointmentLink } from './AppointmentLink';
import { AppointmentsState } from '../../appointments/AppointmentsState';
import { IAppointment } from '../../appointments/IAppointment';
import { TagsSection } from './TagsSection';
import { SummaryFeedback } from './SummaryFeedback';
import { IRecording } from '../../recording/RecordingState';
import { ICaremapNoteResponse } from '../model/NotesClient';
import { hasExtractedFields } from './hasExtractedFields';
import { IOSTabs } from '../../components/IOSTabs';
import { RecordingPlayer } from './RecordingPlayer';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { CareMapState } from '../../caremap/CareMapState';

type NoteDetailTab = 'summary' | 'recording';

const hasBeaContent = (note: ICaremapNoteResponse): boolean => {
    // otherAttributes is loosely typed; recording is always IRecording when present
    const recording = note.otherAttributes?.recording as IRecording | undefined;
    if (!recording) {
        return false;
    }
    // Intro recordings show a placeholder while processing
    if (recording.summaryPrompt === 'intro'
        && recording.status !== 'summarized' && recording.status !== 'summarize-failed') {
        return true;
    }
    return !!recording.summary?.beaComment
        || hasExtractedFields(recording.extractedFields);
};

const useStyles = createUseStyles({
    stretchHeight: {
        display: 'flex',
        flexDirection: 'column'
    }
});

interface INoteDetailBodyProps {
    noteId: string;
    trackingFrom?: string;
    editHandler: () => void;
    onAppointmentClick: (appointment: IAppointment) => void;
    onCloseDrawer: () => void;
}

export const NoteDetailBody: FC<INoteDetailBodyProps> = ({ noteId, trackingFrom, editHandler, onAppointmentClick, onCloseDrawer }) => {
    const classes = useStyles();

    const tracker = useInstance(CareMapNoteTracker);
    const appointmentsState = useInstance(AppointmentsState);
    const tracking = useInstance(MixPanelEventEmitter);
    const careMapState = useInstance(CareMapState);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const model = notesFactory.forActiveCaremap();
    const note = model.getNote(noteId);

    // Get appointment if linked through recording
    const appointmentId = note?.otherAttributes?.recording?.appointmentId;
    const appointment = appointmentId ? appointmentsState.getAppointment(appointmentId) : undefined;

    const [activeTab, setActiveTab] = useState<NoteDetailTab>('summary');

    useEffect(() => {
        if (note) {
            tracker.onNoteViewed(note, trackingFrom);
        }
    }, [noteId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!note) {
        return;
    }

    const canEdit = model.canEdit(note);

    const handleAppointmentClick = () => {
        if (appointment) {
            onAppointmentClick(appointment);
        }
    };

    const recording = note.otherAttributes?.recording as IRecording | undefined;
    // Presence of a CDN url is the only signal we use to decide whether to show
    // the tabs — recordings still being processed (status: 'uploaded' /
    // 'transcribed') already have a playable url and getting to the audio
    // earlier is a feature. Failed states (e.g. 'summarize-failed') also have
    // a url, and showing the player there is fine: the audio plays, the
    // summary tab just won't have AI content.
    const hasPlayableRecording = !!recording?.url;

    const handleTabChange = (tab: NoteDetailTab) => {
        if (tab === activeTab) {
            return;
        }
        setActiveTab(tab);
        tracking.track('Note Detail Tab Switched', () => ({
            tab,
            noteId: note.noteId,
            careMapId: careMapState.getActiveCaremap()?.careMapId
        }));
    };

    const showSummary = !hasPlayableRecording || activeTab === 'summary';
    const showRecording = hasPlayableRecording && activeTab === 'recording';

    return (
        <div data-type="view-note" data-note-id={note.noteId} className={classes.stretchHeight}>

            <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
                <div style={{fontSize: 20, fontWeight: 'bold'}}>{note.title}</div>
                <ViewNoteMenu model={model} noteId={noteId} trackingFrom={trackingFrom} onCloseDrawer={onCloseDrawer} customEditHandler={editHandler} />
            </div>

            <NoteDateTarget note={note} showNoteTargetHeader showBothDates />

            {hasPlayableRecording && (
                <div style={{ margin: '12px 0' }} data-id="note-detail-tabs">
                    <IOSTabs<NoteDetailTab> value={activeTab} onChange={handleTabChange}>
                        <IonSegmentButton value="summary" data-id="note-detail-tab-summary">
                            <IonLabel>Summary</IonLabel>
                        </IonSegmentButton>
                        <IonSegmentButton value="recording" data-id="note-detail-tab-recording">
                            <IonLabel>Recording</IonLabel>
                        </IonSegmentButton>
                    </IOSTabs>
                </div>
            )}

            {showSummary && (
                <>
                    {note.otherAttributes?.recording?.summaryPrompt !== 'intro' && (
                        <UserNotes
                            content={note.html}
                            editHandler={canEdit ? editHandler : undefined}
                            hasAISummary={!!note.otherAttributes?.recording?.summary?.detailedSummary}
                        />
                    )}

                    {appointment && (
                        <AppointmentLink
                            appointment={appointment}
                            onClick={handleAppointmentClick}
                        />
                    )}

                    {hasBeaContent(note) ? (
                        <BeaCommentBubble note={note} onCloseDrawer={onCloseDrawer} />
                    ) : (
                        <ShareNoteNudge note={note} />
                    )}

                    {note.otherAttributes?.recording?.summaryPrompt === 'intro' && (
                        <UserNotes
                            content={note.html}
                            editHandler={canEdit ? editHandler : undefined}
                            hasAISummary={!!note.otherAttributes?.recording?.summary?.detailedSummary}
                        />
                    )}

                    <Overview note={note} />

                    {note.otherAttributes?.recording?.summaryPrompt !== 'intro' && (
                        <Participants note={note} />
                    )}

                    <DetailedSummary note={note} />

                    <SmartTasks note={note} />

                    <SummaryFeedback note={note} />

                    <Attachments
                        note={note}
                        editHandler={canEdit ? editHandler : undefined}
                    />

                    <TagsSection note={note} />

                    {!hasPlayableRecording && <Transcription note={note} />}
                </>
            )}

            {showRecording && recording && (
                <>
                    <RecordingPlayer recording={recording} noteId={note.noteId} />
                    <Transcription note={note} />
                </>
            )}
        </div>
    );
};
